<template>
  <div class="batch-facts">
    <div class="batch-ident">
      <div class="ident-plan">
        <span class="ident-insurance">{{ batch.insurance.name }}</span>
        <span class="ident-name">{{ batch.insurance.plan.name }}</span>
        <span class="ident-meta" v-if="batch.insurance.registroANS">
          ANS {{ batch.insurance.registroANS }}
        </span>
        <span class="ident-meta">{{ tissLabel }}</span>
        <span class="chip chip-amber" v-if="outdated">versão desatualizada</span>
      </div>
      <div class="ident-guides">
        <span class="cr-type" :class="`cr-type-${batch.guideType}`">
          {{ guideTypeName(batch.guideType) }}
        </span>
        <span class="ident-quantity">
          {{ batch.quantity }} {{ batch.quantity === 1 ? 'guia' : 'guias' }}
        </span>
      </div>
    </div>

    <div class="batch-records">
      <div class="batch-record" v-if="showProtocol" :class="{'is-editing': editing === 'protocol'}">
        <div class="record-label">Protocolo</div>
        <div class="record-body" v-if="editing === 'protocol'">
          <div class="fact-editor">
            <input type="text" class="form-input input-sm" maxlength="20" placeholder="Número"
                   v-model="draft.number" @keypress.enter="save('protocol')">
            <dx-input-date class="form-input input-sm" placeholder="00/00/0000"
                           v-model="draft.date"/>
            <button class="btn btn-sm btn-primary btn-action btn-icon" :class="{loading: saving}"
                    :disabled="saving" @click="save('protocol')">
              <fa-icon :icon="['fal', 'check']"/>
            </button>
            <button class="btn btn-sm btn-gray btn-action btn-icon" :disabled="saving"
                    @click="cancel">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
          </div>
        </div>
        <template v-else>
          <div class="record-body">
            <span class="record-value num" :class="{'is-empty': !protocol.number}">
              {{ protocol.number || 'Não informado' }}
            </span>
            <span class="record-hint" v-if="protocol.date">
              recebido em {{ protocol.date | date }}
            </span>
            <span class="record-hint" v-if="protocol.situation">{{ protocol.situation }}</span>
          </div>
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left record-action"
                  :class="{'is-visible': !protocol.number}"
                  v-if="editable.protocol" @click="edit('protocol')">
            <fa-icon :icon="['fal', 'pencil']"/>{{ protocol.number ? 'Editar' : 'Informar' }}
          </button>
        </template>
      </div>

      <div class="batch-record" :class="{'is-editing': editing === 'expectedPaymentAt'}">
        <div class="record-label">Previsão de recebimento</div>
        <div class="record-body" v-if="editing === 'expectedPaymentAt'">
          <div class="fact-editor" :class="{'has-error': $v.draft.date.$error}">
            <dx-input-date class="form-input input-sm" placeholder="00/00/0000" v-model="draft.date"
                           @blur="$v.draft.date.$touch()"/>
            <button class="btn btn-sm btn-primary btn-action btn-icon" :class="{loading: saving}"
                    :disabled="saving" @click="save('expectedPaymentAt')">
              <fa-icon :icon="['fal', 'check']"/>
            </button>
            <button class="btn btn-sm btn-gray btn-action btn-icon" :disabled="saving"
                    @click="cancel">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
            <div class="form-input-hint" v-if="$v.draft.date.$error">Data inválida</div>
            <div class="form-input-hint" v-else>
              Ao informar, a regra do plano deixa de recalcular
            </div>
          </div>
        </div>
        <template v-else>
          <div class="record-body">
            <span class="record-value" :class="{'is-empty': !expectedPaymentAt}">
              {{ expectedPaymentAt ? dateFilter(expectedPaymentAt) : 'Sem previsão' }}
            </span>
            <span class="record-hint" v-if="expectedDescription">{{ expectedDescription }}</span>
          </div>
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left record-action"
                  :class="{'is-visible': !expectedPaymentAt}"
                  v-if="editable.expectedPaymentAt" @click="edit('expectedPaymentAt')">
            <fa-icon :icon="['fal', 'pencil']"/>{{ expectedPaymentAt ? 'Editar' : 'Informar' }}
          </button>
        </template>
      </div>

      <div class="batch-record" :class="{'is-editing': editing === 'invoice'}">
        <div class="record-label">Nota fiscal</div>
        <div class="record-body" v-if="editing === 'invoice'">
          <div class="fact-editor fact-editor-wrap"
               :class="{'has-error': $v.draft.number.$error || $v.draft.date.$error}">
            <input type="text" class="form-input input-sm" maxlength="30" placeholder="Nº da NF"
                   v-model="draft.number" @blur="$v.draft.number.$touch()"
                   @keypress.enter="save('invoice')">
            <dx-input-date class="form-input input-sm" placeholder="Emissão" v-model="draft.date"
                           @blur="$v.draft.date.$touch()"/>
            <button class="btn btn-sm btn-primary btn-action btn-icon" :class="{loading: saving}"
                    :disabled="saving" @click="save('invoice')">
              <fa-icon :icon="['fal', 'check']"/>
            </button>
            <button class="btn btn-sm btn-gray btn-action btn-icon" :disabled="saving"
                    @click="cancel">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
            <div class="form-input-hint" v-if="$v.draft.number.$error">Campo obrigatório</div>
            <div class="form-input-hint" v-else-if="$v.draft.date.$error">Data inválida</div>
          </div>
        </div>
        <template v-else>
          <div class="record-body">
            <span class="record-value num" :class="{'is-empty': !invoice.number}">
              {{ invoice.number ? `Nº ${invoice.number}` : 'Não registrada' }}
            </span>
            <span class="record-hint" v-if="invoice.number && invoice.sentAt">
              emitida em {{ invoice.sentAt | date }}
            </span>
            <span class="record-warn" v-if="invoicePending">
              <fa-icon :icon="['fal', 'exclamation-triangle']"/>
              Obrigatória antes do pagamento{{ invoiceDeadline }}
            </span>
            <span class="record-hint" v-else-if="invoice.requiredBeforePayment">
              exigida pela operadora
            </span>
          </div>
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left record-action"
                  :class="{'is-visible': !invoice.number}"
                  v-if="editable.invoice" @click="edit('invoice')">
            <fa-icon :icon="['fal', invoice.number ? 'pencil' : 'file-invoice-dollar']"/>
            {{ invoice.number ? 'Editar' : 'Registrar' }}
          </button>
        </template>
      </div>

      <div class="batch-record" :class="{'is-editing': editing === 'notes'}">
        <div class="record-label">Observações</div>
        <div class="record-body" v-if="editing === 'notes'">
          <div class="fact-editor">
            <textarea class="form-input" rows="2" maxlength="500" v-model="draft.text"></textarea>
            <button class="btn btn-sm btn-primary btn-action btn-icon" :class="{loading: saving}"
                    :disabled="saving" @click="save('notes')">
              <fa-icon :icon="['fal', 'check']"/>
            </button>
            <button class="btn btn-sm btn-gray btn-action btn-icon" :disabled="saving"
                    @click="cancel">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
          </div>
        </div>
        <template v-else>
          <div class="record-body">
            <span class="record-value text-pre" :class="{'is-empty': !batch.notes}">
              {{ batch.notes || 'Sem observações' }}
            </span>
          </div>
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left record-action"
                  :class="{'is-visible': !batch.notes}"
                  v-if="editable.notes" @click="edit('notes')">
            <fa-icon :icon="['fal', 'pencil']"/>{{ batch.notes ? 'Editar' : 'Informar' }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { requiredIf } from 'vuelidate/lib/validators';
import { date } from '@/data/validators';
import dateFilter from '@/filters/date';
import {
  guideTypeName,
  tissOutdated,
  expectedPaymentDescription,
} from '../service';

const TOASTS = {
  protocol: 'Protocolo registrado.',
  expectedPaymentAt: 'Previsão de recebimento atualizada.',
  invoice: 'Nota fiscal registrada.',
  notes: 'Observações atualizadas.',
};

const FINAL_STATUSES = ['settled', 'canceled'];

export default {
  props: {
    batch: {
      type: Object,
      required: true,
    },
    editable: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      editing: null,
      saving: false,
      draft: { number: '', date: '', text: '' },
    };
  },
  computed: {
    protocol() {
      return this.batch.protocol || {};
    },
    invoice() {
      return this.batch.invoice || {};
    },
    invoicePending() {
      return !!this.invoice.requiredBeforePayment && !this.invoice.number
        && !FINAL_STATUSES.includes(this.batch.status);
    },
    invoiceDeadline() {
      return this.invoice.deadlineAt ? ` · até ${dateFilter(this.invoice.deadlineAt)}` : '';
    },
    expectedPaymentAt() {
      return this.batch.dates.expectedPaymentAt;
    },
    expectedDescription() {
      return expectedPaymentDescription(this.batch);
    },
    outdated() {
      return tissOutdated(this.batch.tiss);
    },
    tissEnabled() {
      return !!(this.batch.tiss && this.batch.tiss.enabled);
    },
    tissLabel() {
      if (!this.tissEnabled) return 'sem TISS';
      return `TISS ${this.batch.tiss.version || '—'}`;
    },
    showProtocol() {
      return !!this.protocol.number || !!this.editable.protocol;
    },
  },
  validations() {
    return {
      draft: {
        number: { required: requiredIf(() => this.editing === 'invoice') },
        date: { date },
      },
    };
  },
  methods: {
    guideTypeName,
    dateFilter,
    toInputDate(value) {
      return value ? moment(value).format('YYYY-MM-DD') : '';
    },
    edit(field) {
      if (field === 'protocol') {
        this.draft = {
          number: this.protocol.number || '',
          date: this.toInputDate(this.protocol.date),
          text: '',
        };
      } else if (field === 'expectedPaymentAt') {
        this.draft = {
          number: '',
          date: this.toInputDate(this.batch.dates.expectedPaymentAt),
          text: '',
        };
      } else if (field === 'invoice') {
        this.draft = {
          number: this.invoice.number || '',
          date: this.toInputDate(this.invoice.sentAt) || moment().format('YYYY-MM-DD'),
          text: '',
        };
      } else {
        this.draft = { number: '', date: '', text: this.batch[field] || '' };
      }
      this.$v.draft.$reset();
      this.editing = field;
    },
    cancel() {
      this.editing = null;
    },
    payload(field) {
      if (field === 'protocol') {
        return {
          protocol: { number: this.draft.number.trim() || null, date: this.draft.date || null },
        };
      }
      if (field === 'expectedPaymentAt') {
        return { dates: { expectedPaymentAt: this.draft.date || null } };
      }
      return { [field]: this.draft.text.trim() || null };
    },
    request(field) {
      if (field === 'invoice') {
        return this.$http.post(`/billing-batches/${this.batch.id}/invoice`, {
          number: this.draft.number.trim(),
          sentAt: this.draft.date || null,
        });
      }
      return this.$http.put(`/billing-batches/${this.batch.id}`, this.payload(field));
    },
    save(field) {
      this.$v.draft.$touch();
      if (this.$v.draft.$error) return;

      this.saving = true;
      this.request(field)
        .then(({ data }) => {
          this.$toast.success(TOASTS[field]);
          this.editing = null;
          this.$emit('saved', { field, batch: data });
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
$amber-text: #8A6D1F;

.batch-facts {
  border: $border-color solid $border-width;
  border-radius: $border-radius;
  margin-top: $layout-spacing-lg;
  overflow: hidden;
  .batch-ident {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-bottom: $border-color solid $border-width;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    .ident-plan {
      align-items: center;
      display: flex;
      flex: 1 1 auto;
      flex-wrap: wrap;
      gap: .3rem;
      min-width: 0;
    }
    .ident-insurance {
      font-size: $font-size-lg;
      font-weight: 600;
    }
    .ident-name {
      font-size: $font-size-sm;
      font-weight: 600;
      &::before {
        color: $border-color-dark;
        content: '·';
        margin-right: .3rem;
      }
    }
    .ident-meta {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      &::before {
        color: $border-color-dark;
        content: '·';
        margin-right: .3rem;
      }
    }
    .ident-guides {
      align-items: center;
      display: flex;
      flex: 0 0 auto;
      gap: .3rem;
      .ident-quantity {
        font-size: $font-size-sm;
        font-weight: 600;
      }
    }
  }
  .batch-records {
    background-color: $light-color;
  }
  .batch-record {
    align-items: center;
    border-bottom: $border-color solid $border-width;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: 9.5rem minmax(0, 1fr) auto;
    min-height: 1.9rem;
    padding: .25rem $layout-spacing-lg;
    &:last-child {
      border-bottom: 0;
    }
    &.is-editing .record-body {
      grid-column: 2 / -1;
    }
    .record-label {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }
    .record-body {
      align-items: baseline;
      display: flex;
      flex-wrap: wrap;
      gap: .3rem;
      min-width: 0;
    }
    .record-value {
      font-size: $font-size-sm;
      font-weight: 600;
      overflow-wrap: anywhere;
      &.is-empty {
        color: $gray-color-dark;
        font-weight: 400;
      }
    }
    .record-hint {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      &::before {
        color: $border-color-dark;
        content: '·';
        margin-right: .3rem;
      }
    }
    .record-warn {
      color: $amber-text;
      font-size: $font-size-sm;
      font-weight: 600;
      svg {
        margin-right: .15rem;
      }
    }
    .record-action {
      opacity: 0;
      transition: opacity .1s;
    }
    &:hover .record-action,
    .record-action.is-visible,
    .record-action:focus {
      opacity: 1;
    }
  }
  .fact-editor {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    width: 100%;
    .form-input {
      height: 1.4rem;
    }
    textarea.form-input {
      height: auto;
    }
    .form-input-hint {
      flex-basis: 100%;
      margin: 0;
    }
    &.fact-editor-wrap .form-input {
      flex: 1 1 5rem;
      min-width: 0;
    }
  }
  @media screen and (max-width: $size-sm) {
    .batch-record {
      grid-template-columns: minmax(0, 1fr) auto;
      .record-label {
        grid-column: 1 / -1;
      }
      .record-action {
        opacity: 1;
      }
    }
  }
}
</style>
